import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import {
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  InputAdornment,
  Grid,
  Pagination,
  Stack,
  TextField as MuiTextField,
  Popover,
  Tooltip,
  Checkbox,
  FormControlLabel,
  FormGroup
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Discount as DiscountIcon,
  Search as SearchIcon,
  Delete as DeleteIcon,
  DeleteSweep as DeleteSweepIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';

const FabricDiscountManager = () => {
  const [fabrics, setFabrics] = useState([]);
  const [filteredFabrics, setFilteredFabrics] = useState([]);
  const [originalFabrics, setOriginalFabrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [page, setPage] = useState(1);
  const [showOnlyWithDiscount, setShowOnlyWithDiscount] = useState(false);
  
  const itemsPerPage = 20;
  
  
  const [filters, setFilters] = useState({});
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [currentFilterColumn, setCurrentFilterColumn] = useState(null);
  const [filterValue, setFilterValue] = useState('');
  
  
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedFabric, setSelectedFabric] = useState(null);
  const [discountValue, setDiscountValue] = useState('');
  const [saving, setSaving] = useState(false);
  
  
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkData, setBulkData] = useState({
    codesFrom: '',
    codesTo: '',
    discount: ''
  });
  const [bulkLoading, setBulkLoading] = useState(false);
  
  
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
  
  
  const [selectedRows, setSelectedRows] = useState(new Set());
  
  const API_BASE_URL = 'https://test2.planfix-to-syrve.com';
  
  
  const columns = [
    { key: 'code', label: 'Код тканини' },
    { key: 'fabricType', label: 'Тип тканини' },
    { key: 'priceGroup', label: 'Цінова група' },
    { key: 'width', label: 'Ширина' },
    { key: 'discount', label: 'Знижка (%)' }
  ];
  
  useEffect(() => {
    fetchFabrics();
  }, []);
  
  
  const applyFilters = useCallback(() => {
    let filtered = [...originalFabrics];
    
    
    for (const [column, filterValue] of Object.entries(filters)) {
      if (filterValue && filterValue.trim() !== '') {
        const searchTerm = filterValue.toLowerCase().trim();
        filtered = filtered.filter(item => {
          let cellValue = '';
          if (column === 'discount') {
            cellValue = item.discount?.toString() || '0';
          } else {
            cellValue = item[column]?.toString() || '';
          }
          return cellValue.toLowerCase().includes(searchTerm);
        });
      }
    }
    
    
    if (showOnlyWithDiscount) {
      filtered = filtered.filter(item => item.discount > 0);
    }
    
    setFilteredFabrics(filtered);
    setPage(1);
    setSelectedRows(new Set()); 
  }, [filters, originalFabrics, showOnlyWithDiscount]);
  
  
  const clearAllFilters = useCallback(() => {
    setFilters({});
    setShowOnlyWithDiscount(false);
    setFilteredFabrics([...originalFabrics]);
    setFilterAnchorEl(null);
    setCurrentFilterColumn(null);
    setFilterValue('');
    setSelectedRows(new Set());
  }, [originalFabrics]);
  
  
  const clearColumnFilter = useCallback((columnKey) => {
    const newFilters = { ...filters };
    delete newFilters[columnKey];
    setFilters(newFilters);
  }, [filters]);
  
  
  const applyColumnFilter = useCallback(() => {
    if (currentFilterColumn !== null) {
      setFilters(prev => ({
        ...prev,
        [currentFilterColumn]: filterValue
      }));
      setFilterAnchorEl(null);
      setCurrentFilterColumn(null);
      setFilterValue('');
    }
  }, [currentFilterColumn, filterValue]);
  
  
  const openFilterPopover = (event, columnKey) => {
    setFilterAnchorEl(event.currentTarget);
    setCurrentFilterColumn(columnKey);
    setFilterValue(filters[columnKey] || '');
  };
  
  const closeFilterPopover = () => {
    setFilterAnchorEl(null);
    setCurrentFilterColumn(null);
    setFilterValue('');
  };
  
  
  useEffect(() => {
    if (originalFabrics.length > 0) {
      applyFilters();
    }
  }, [filters, originalFabrics, applyFilters, showOnlyWithDiscount]);
  
  const fetchFabrics = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get(`${API_BASE_URL}/api/fabrics/all`, {
        timeout: 30000
      });
      if (response.data.success) {
        const discountsResponse = await axios.get(`${API_BASE_URL}/api/fabrics/discounts`);
        const discounts = discountsResponse.data.data || {};
        
        const fabricsWithDiscounts = response.data.data.map(fabric => ({
          ...fabric,
          discount: discounts[fabric.code] || 0
        }));
        
        setOriginalFabrics(fabricsWithDiscounts);
        setFabrics(fabricsWithDiscounts);
        setFilteredFabrics(fabricsWithDiscounts);
        setFilters({});
        setShowOnlyWithDiscount(false);
      } else {
        setError('Помилка завантаження даних');
      }
    } catch (err) {
      console.error('Error fetching fabrics:', err);
      setError('Помилка при завантаженні даних. Спробуйте пізніше.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleEditDiscount = (fabric) => {
    setSelectedFabric(fabric);
    setDiscountValue(fabric.discount?.toString() || '0');
    setEditDialogOpen(true);
  };
  
  const handleSaveDiscount = async () => {
    if (!selectedFabric) return;
    
    try {
      setSaving(true);
      const response = await axios.post(`${API_BASE_URL}/api/fabrics/save-discount`, {
        fabricCode: selectedFabric.code,
        discount: parseFloat(discountValue) || 0
      });
      
      if (response.data.success) {
        setSuccess('Знижку успішно збережено');
        await fetchFabrics();
        setEditDialogOpen(false);
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(response.data.error || 'Помилка збереження');
      }
    } catch (err) {
      console.error('Error saving discount:', err);
      setError('Помилка при збереженні знижки');
    } finally {
      setSaving(false);
    }
  };
  
  const handleDeleteDiscount = async () => {
    if (!selectedFabric) return;
    
    try {
      setSaving(true);
      const response = await axios.delete(`${API_BASE_URL}/api/fabrics/delete-discount`, {
        data: { fabricCode: selectedFabric.code }
      });
      
      if (response.data.success) {
        setSuccess('Знижку видалено');
        await fetchFabrics();
        setEditDialogOpen(false);
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(response.data.error || 'Помилка видалення');
      }
    } catch (err) {
      console.error('Error deleting discount:', err);
      setError('Помилка при видаленні знижки');
    } finally {
      setSaving(false);
    }
  };
  
  const handleBulkAddDiscount = async () => {
    if (!bulkData.codesFrom || !bulkData.codesTo || !bulkData.discount) {
      setError('Будь ласка, заповніть всі поля');
      return;
    }
    
    try {
      setBulkLoading(true);
      const response = await axios.post(`${API_BASE_URL}/api/fabrics/bulk-discount`, {
        codesFrom: bulkData.codesFrom.toUpperCase(),
        codesTo: bulkData.codesTo.toUpperCase(),
        discount: parseFloat(bulkData.discount)
      });
      
      if (response.data.success) {
        setSuccess(response.data.message);
        await fetchFabrics();
        setBulkDialogOpen(false);
        setBulkData({
          codesFrom: '',
          codesTo: '',
          discount: ''
        });
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(response.data.error || 'Помилка додавання знижок');
      }
    } catch (err) {
      console.error('Error bulk adding discounts:', err);
      setError('Помилка при масовому додаванні знижок');
    } finally {
      setBulkLoading(false);
    }
  };
  
  
  const handleSelectAndDeleteAllWithDiscount = () => {
    const fabricsWithDiscount = filteredFabrics.filter(f => f.discount > 0);
    if (fabricsWithDiscount.length === 0) {
      setError('Немає кодів зі знижкою для видалення');
      return;
    }
    
    const newSet = new Set();
    fabricsWithDiscount.forEach(fabric => {
      newSet.add(fabric.code);
    });
    setSelectedRows(newSet);
    
    setBulkDeleteDialogOpen(true);
  };
  
  
  const handleBulkDeleteDiscounts = async () => {
    if (selectedRows.size === 0) {
      setError('Будь ласка, виберіть хоча б один код для видалення знижки');
      return;
    }
    
    try {
      setBulkDeleteLoading(true);
      const codesToDelete = Array.from(selectedRows);
      
      
      for (const code of codesToDelete) {
        await axios.delete(`${API_BASE_URL}/api/fabrics/delete-discount`, {
          data: { fabricCode: code }
        });
      }
      
      setSuccess(`Видалено знижки для ${codesToDelete.length} кодів`);
      await fetchFabrics();
      setBulkDeleteDialogOpen(false);
      setSelectedRows(new Set());
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error bulk deleting discounts:', err);
      setError('Помилка при масовому видаленні знижок');
    } finally {
      setBulkDeleteLoading(false);
    }
  };
  
  
  const handleSelectRow = (code) => {
    setSelectedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(code)) {
        newSet.delete(code);
      } else {
        newSet.add(code);
      }
      return newSet;
    });
  };
  
  
  const handleSelectAll = () => {
    if (selectedRows.size === paginatedFabrics.length) {
      setSelectedRows(new Set());
    } else {
      const newSet = new Set();
      paginatedFabrics.forEach(fabric => {
        if (fabric.discount > 0) {
          newSet.add(fabric.code);
        }
      });
      setSelectedRows(newSet);
    }
  };
  
  
  const paginatedFabrics = useMemo(() => {
    const start = (page - 1) * itemsPerPage;
    return filteredFabrics.slice(start, start + itemsPerPage);
  }, [filteredFabrics, page]);
  
  const pageCount = Math.ceil(filteredFabrics.length / itemsPerPage);
  const activeFiltersCount = Object.keys(filters).filter(key => filters[key] && filters[key].trim() !== '').length + (showOnlyWithDiscount ? 1 : 0);
  const isFilterOpen = Boolean(filterAnchorEl);
  const hasSelectedRows = selectedRows.size > 0;
  const fabricsWithDiscountCount = filteredFabrics.filter(f => f.discount > 0).length;
  
  
  const getCellValue = (fabric, columnKey) => {
    if (columnKey === 'discount') {
      return fabric.discount > 0 ? `${fabric.discount}%` : '0%';
    }
    return fabric[columnKey] || '—';
  };
  
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px" sx={{ mt: 8 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Завантаження даних...</Typography>
      </Box>
    );
  }
  
  return (
    <Box sx={{ p: 2, maxWidth: 1400, margin: '0 auto', mt: 8 }}>
    
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}
      
      {}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Пошук за кодом тканини"
              variant="outlined"
              size="small"
              value={filters.code || ''}
              onChange={(e) => setFilters(prev => ({ ...prev, code: e.target.value }))}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              placeholder="Наприклад: A027, B015, C123"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              startIcon={<DiscountIcon />}
              onClick={() => setBulkDialogOpen(true)}
              sx={{ height: 40 }}
            >
              Додати знижки
            </Button>
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              fullWidth
              variant="contained"
              color="error"
              startIcon={<DeleteSweepIcon />}
              onClick={handleSelectAndDeleteAllWithDiscount}
              disabled={fabricsWithDiscountCount === 0}
              sx={{ height: 40 }}
            >
              Видалити всі знижки ({fabricsWithDiscountCount})
            </Button>
          </Grid>
          <Grid item xs={12} md={3}>
            {activeFiltersCount > 0 && (
              <Button
                fullWidth
                variant="outlined"
                color="error"
                startIcon={<ClearIcon />}
                onClick={clearAllFilters}
                sx={{ height: 40 }}
              >
                Очистити всі фільтри ({activeFiltersCount})
              </Button>
            )}
          </Grid>
        </Grid>
      </Paper>
      
      {}
      <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <Chip label={`Всього кодів: ${originalFabrics.length}`} color="primary" variant="outlined" />
        <Chip label={`Відфільтровано: ${filteredFabrics.length}`} color="secondary" variant="outlined" />
        <Chip 
          label={`Зі знижкою: ${originalFabrics.filter(f => f.discount > 0).length}`} 
          color="success" 
          variant={showOnlyWithDiscount ? "filled" : "outlined"}
          onClick={() => setShowOnlyWithDiscount(!showOnlyWithDiscount)}
          sx={{ cursor: 'pointer' }}
        />
        {hasSelectedRows && (
          <Chip 
            label={`Вибрано: ${selectedRows.size}`} 
            color="warning" 
            variant="filled"
          />
        )}
      </Box>
      
      {}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell sx={{ width: 40, fontWeight: 'bold' }}>
                <Checkbox
                  checked={paginatedFabrics.length > 0 && selectedRows.size === paginatedFabrics.filter(f => f.discount > 0).length}
                  indeterminate={selectedRows.size > 0 && selectedRows.size < paginatedFabrics.filter(f => f.discount > 0).length}
                  onChange={handleSelectAll}
                  disabled={paginatedFabrics.filter(f => f.discount > 0).length === 0}
                />
              </TableCell>
              {columns.map((column) => {
                const hasFilter = filters[column.key] && filters[column.key].trim() !== '';
                return (
                  <TableCell key={column.key} sx={{ fontWeight: 'bold' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                      <span>{column.label}</span>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title={hasFilter ? `Фільтр: ${filters[column.key]}` : 'Фільтрувати'}>
                          <IconButton
                            size="small"
                            onClick={(e) => openFilterPopover(e, column.key)}
                            sx={{ 
                              p: 0.5,
                              color: hasFilter ? 'primary.main' : 'grey.500'
                            }}
                          >
                            <FilterIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {hasFilter && (
                          <Tooltip title="Очистити фільтр">
                            <IconButton
                              size="small"
                              onClick={() => clearColumnFilter(column.key)}
                              sx={{ p: 0.5, color: 'error.main' }}
                            >
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                );
              })}
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>Дії</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedFabrics.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length + 2} align="center">
                  <Typography color="textSecondary" sx={{ py: 4 }}>
                    Коди не знайдено
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedFabrics.map((fabric) => (
                <TableRow key={fabric.code} hover>
                  <TableCell>
                    <Checkbox
                      checked={selectedRows.has(fabric.code)}
                      onChange={() => handleSelectRow(fabric.code)}
                      disabled={fabric.discount === 0}
                    />
                  </TableCell>
                  {columns.map((column) => (
                    <TableCell key={column.key}>
                      {column.key === 'discount' ? (
                        <Chip
                          label={getCellValue(fabric, column.key)}
                          color={fabric.discount > 0 ? 'warning' : 'default'}
                          size="small"
                          sx={{ fontWeight: 'bold', minWidth: '60px' }}
                        />
                      ) : column.key === 'priceGroup' ? (
                        <Chip 
                          label={getCellValue(fabric, column.key)} 
                          size="small" 
                          color={fabric.priceGroup === 'A' ? 'success' : 'default'}
                        />
                      ) : column.key === 'code' ? (
                        <Typography variant="body1" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                          {getCellValue(fabric, column.key)}
                        </Typography>
                      ) : (
                        getCellValue(fabric, column.key)
                      )}
                    </TableCell>
                  ))}
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleEditDiscount(fabric)}
                      title="Редагувати знижку"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    {fabric.discount > 0 && (
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => {
                          setSelectedFabric(fabric);
                          handleDeleteDiscount();
                        }}
                        title="Видалити знижку"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      
      {}
      {pageCount > 1 && (
        <Stack spacing={2} alignItems="center" sx={{ mt: 3 }}>
          <Pagination 
            count={pageCount} 
            page={page} 
            onChange={(e, value) => setPage(value)}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Stack>
      )}
      
      {}
      <Popover
        open={isFilterOpen}
        anchorEl={filterAnchorEl}
        onClose={closeFilterPopover}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Box sx={{ p: 2, width: 300 }}>
          <Typography variant="subtitle2" gutterBottom>
            Фільтр для колонки: {columns.find(c => c.key === currentFilterColumn)?.label || ''}
          </Typography>
          <TextField
            fullWidth
            size="small"
            label="Значення для пошуку"
            value={filterValue}
            onChange={(e) => setFilterValue(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                applyColumnFilter();
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            autoFocus
          />
          <Box sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button size="small" onClick={closeFilterPopover}>
              Скасувати
            </Button>
            <Button size="small" variant="contained" onClick={applyColumnFilter}>
              Застосувати
            </Button>
          </Box>
        </Box>
      </Popover>
      
      {}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Редагування знижки
          <IconButton
            onClick={() => setEditDialogOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" color="textSecondary">Код тканини:</Typography>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', fontFamily: 'monospace', mb: 3 }}>
              {selectedFabric?.code}
            </Typography>
            
            <TextField
              fullWidth
              label="Знижка (%)"
              type="number"
              value={discountValue}
              onChange={(e) => setDiscountValue(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start">%</InputAdornment>,
              }}
              autoFocus
              helperText="Введіть число від 0 до 100"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} color="secondary">
            Скасувати
          </Button>
          <Button 
            onClick={handleSaveDiscount} 
            variant="contained" 
            color="primary"
            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
            disabled={saving}
          >
            {saving ? 'Збереження...' : 'Зберегти'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {}
      <Dialog open={bulkDialogOpen} onClose={() => setBulkDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Масове додавання знижок за діапазоном
          <IconButton
            onClick={() => setBulkDialogOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Alert severity="info" sx={{ mb: 3 }}>
              <strong>Приклад:</strong> для діапазону <strong>A027 - A056</strong> будуть додані всі коди:<br/>
              A027, A028, A029, A030, ..., A056
              <br/><br/>
              <strong>Важливо:</strong> знижки будуть додані ТІЛЬКИ для кодів, які реально існують в переліку тканин.
            </Alert>
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={5}>
                <MuiTextField
                  fullWidth
                  label="Код ВІД"
                  placeholder="A027"
                  value={bulkData.codesFrom}
                  onChange={(e) => setBulkData({...bulkData, codesFrom: e.target.value.toUpperCase()})}
                  helperText="Наприклад: A027"
                />
              </Grid>
              <Grid item xs={12} md={2} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="h5">—</Typography>
              </Grid>
              <Grid item xs={12} md={5}>
                <MuiTextField
                  fullWidth
                  label="Код ДО"
                  placeholder="A056"
                  value={bulkData.codesTo}
                  onChange={(e) => setBulkData({...bulkData, codesTo: e.target.value.toUpperCase()})}
                  helperText="Наприклад: A056"
                />
              </Grid>
            </Grid>
            
            <MuiTextField
              fullWidth
              label="Знижка (%)"
              type="number"
              placeholder="10"
              value={bulkData.discount}
              onChange={(e) => setBulkData({...bulkData, discount: e.target.value})}
              sx={{ mt: 3 }}
              helperText="Введіть розмір знижки для всіх кодів у діапазоні"
              InputProps={{
                startAdornment: <InputAdornment position="start">%</InputAdornment>,
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDialogOpen(false)} color="secondary">
            Скасувати
          </Button>
          <Button 
            onClick={handleBulkAddDiscount} 
            variant="contained" 
            color="primary"
            startIcon={bulkLoading ? <CircularProgress size={20} /> : <DiscountIcon />}
            disabled={bulkLoading}
          >
            {bulkLoading ? 'Додавання...' : 'Додати знижки для діапазону'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {}
      <Dialog open={bulkDeleteDialogOpen} onClose={() => setBulkDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Підтвердження масового видалення
          <IconButton
            onClick={() => setBulkDeleteDialogOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Alert severity="warning" sx={{ mb: 2 }}>
              <strong>Увага!</strong> Ви збираєтесь видалити знижки для <strong>{selectedRows.size}</strong> кодів.
            </Alert>
            <Typography variant="body2">
              Цю дію неможливо буде скасувати. Ви впевнені, що хочете продовжити?
            </Typography>
            {selectedRows.size > 0 && (
              <Box sx={{ mt: 2, maxHeight: 200, overflow: 'auto' }}>
                <Typography variant="subtitle2">Коди для видалення:</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                  {Array.from(selectedRows).slice(0, 20).map(code => (
                    <Chip key={code} label={code} size="small" variant="outlined" />
                  ))}
                  {selectedRows.size > 20 && (
                    <Chip label={`+ ще ${selectedRows.size - 20}`} size="small" />
                  )}
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDeleteDialogOpen(false)} color="secondary">
            Скасувати
          </Button>
          <Button 
            onClick={handleBulkDeleteDiscounts} 
            variant="contained" 
            color="error"
            startIcon={bulkDeleteLoading ? <CircularProgress size={20} /> : <DeleteSweepIcon />}
            disabled={bulkDeleteLoading}
          >
            {bulkDeleteLoading ? 'Видалення...' : 'Так, видалити знижки'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FabricDiscountManager;
